
import React, { useState, useRef, useEffect } from 'react';
import { Send, User, Sparkles, Loader2, Trash2, PlusCircle } from 'lucide-react';
import { ChatMessage, UserProfile } from '../types.ts';
import { PERSONALITY_CONFIG } from '../constants.tsx';
import { TRANSLATIONS } from '../translations.ts';
import { sendMessageToHawre } from '../services/geminiService.ts';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

const ChatInterface: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [messages, setMessages] = useState<ChatMessage[]>(user.chatHistory || []);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const t = TRANSLATIONS[user.language];
  const personality = PERSONALITY_CONFIG[user.selectedPersonality];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const updatePersistentHistory = (newMessages: ChatMessage[]) => {
    onUpdateUser({
      ...user,
      chatHistory: newMessages
    });
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: input,
      timestamp: Date.now(),
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    updatePersistentHistory(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      const response = await sendMessageToHawre(user, messages, input);
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'model',
        text: response,
        timestamp: Date.now(),
      };
      const finalMessages = [...newMessages, aiMessage];
      setMessages(finalMessages);
      updatePersistentHistory(finalMessages);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = () => {
    if (window.confirm(user.language === 'fa' ? 'آیا مطمئن هستید که می‌خواهید گفتگوی جدیدی را شروع کنید؟' : 'ئایا دڵنیایت کە دەتەوێت گفتوگۆیەکی نوێ دەستپێبکەیت؟')) {
      setMessages([]);
      updatePersistentHistory([]);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden animate-fade-in">
      {/* Header */}
      <div className={`p-4 border-b border-slate-50 ${personality.bg} flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className={`p-2 bg-white rounded-xl shadow-sm ${personality.color}`}>
            {personality.icon}
          </div>
          <div>
            <h3 className="font-bold text-slate-800">{t[personality.labelKey]}</h3>
            <span className="text-[10px] text-slate-500">{t.chat_status}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
           <button 
             onClick={handleNewChat}
             className="p-2 hover:bg-white/50 rounded-lg transition-colors text-slate-600 flex items-center gap-1 text-xs font-bold"
             title={user.language === 'fa' ? 'گفتگوی جدید' : 'گفتوگۆی نوێ'}
           >
             <PlusCircle className="w-4 h-4" />
             <span className="hidden sm:inline">{user.language === 'fa' ? 'گفتگوی جدید' : 'گفتوگۆی نوێ'}</span>
           </button>
           <Sparkles className="w-5 h-5 text-amber-500" />
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/30">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center p-8">
            <div className={`w-16 h-16 rounded-2xl ${personality.bg} ${personality.color} flex items-center justify-center mb-4`}>
              {personality.icon}
            </div>
            <h4 className="font-bold text-slate-800 mb-2">{t.chat_welcome_title}</h4>
            <p className="text-sm text-slate-500 max-w-xs">{t.chat_welcome_desc}</p>
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}>
            <div className={`flex gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row' : 'flex-row-reverse'}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 shadow-sm ${msg.role === 'user' ? 'bg-white text-slate-400' : `${personality.bg} ${personality.color}`}`}>
                {msg.role === 'user' ? <User className="w-5 h-5" /> : personality.icon}
              </div>
              <div className={`p-4 rounded-2xl text-sm leading-relaxed ${msg.role === 'user' ? 'bg-white text-slate-800 rounded-tr-none border border-slate-100 shadow-sm' : `${personality.themeColor === '#ec4899' ? 'bg-pink-600' : personality.themeColor === '#475569' ? 'bg-slate-700' : personality.themeColor === '#f59e0b' ? 'bg-amber-600' : 'bg-emerald-600'} text-white rounded-tl-none shadow-md shadow-slate-200`}`}>
                {msg.text}
              </div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-end">
            <div className="flex gap-2 items-center bg-white p-4 rounded-2xl rounded-tl-none shadow-sm border border-slate-100">
              <Loader2 className="w-4 h-4 animate-spin text-slate-300" />
              <span className="text-xs text-slate-400">{t.chat_thinking}</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 bg-white border-t border-slate-100">
        <div className="flex gap-2 bg-slate-50 rounded-2xl p-2 border border-slate-100 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
          <input 
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder={t.chat_placeholder}
            className="flex-1 bg-transparent border-none outline-none px-3 py-2 text-sm text-slate-800"
          />
          <button 
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="bg-indigo-600 text-white p-3 rounded-xl hover:bg-indigo-700 disabled:opacity-50 shadow-lg shadow-indigo-100 transition-all"
          >
            <Send className="w-5 h-5 rtl:-rotate-90" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
